import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {
  ProductGroupFilterDisplay,
  ProductGroupFilterGroups,
} from '@em/data-feed/data-access-products';
import {EmSelectModule} from '@em/shared/ui';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TreeNode} from 'primeng/api';
import {TreeNodeSelectEvent} from 'primeng/tree';
import {TreeSelectModule} from 'primeng/treeselect';
import {startWith} from 'rxjs/operators';
import {EmCategorySelectionComponent} from '../product-filter/category-selection/category-selection.component';
import {EmProductGroupConditionComponent} from '../product-group-condition/product-group-condition.component';
import {EmCustomLabelsConditionComponent} from '../custom-labels-condition/custom-labels-condition.component';
import {EmBestPriceFilterComponent} from '../best-price-filter/best-price-filter.component';
import {EmMostUsedPriceFilterComponent} from '../most-used-price-filter/most-used-price-filter.component';
import {EmPriceChangesFilterComponent} from '../price-changes-filter/price-changes-filter.component';
import {EmFetchStatusFilterComponent} from '../fetch-status-filter/fetch-status-filter.component';
import {EmOutliersFilterComponent} from '../outliers-filter/outliers-filter.component';
import {EmDaysValueFilterComponent} from '../days-value-filter/days-value-filter.component';
import {EmWithCompetitorsFilterComponent} from '../with-competitors-filter/with-competitors-filter.component';
import {EmProductPerformanceFilterComponent} from '../product-performance-filter/product-performance-filter.component';

@Component({
  selector: 'em-product-group-filter',
  templateUrl: './product-group-filter.component.html',
  styleUrls: ['./product-group-filter.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    EmSelectModule,
    EmCategorySelectionComponent,
    TreeSelectModule,
    EmProductGroupConditionComponent,
    EmCustomLabelsConditionComponent,
    EmBestPriceFilterComponent,
    EmMostUsedPriceFilterComponent,
    EmPriceChangesFilterComponent,
    EmFetchStatusFilterComponent,
    EmOutliersFilterComponent,
    EmDaysValueFilterComponent,
    EmWithCompetitorsFilterComponent,
    EmProductPerformanceFilterComponent,
  ],
})
export class EmProductGroupFilterComponent implements OnInit {
  private readonly _controlContainer = inject(ControlContainer);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _translateService = inject(TranslateService);

  readonly selectedFilterControl = new FormControl<TreeNode | null>(null);

  conditionForm: UntypedFormGroup | null | undefined;
  selectedKey = signal<string | null>(null);
  selectedFilter = signal<ProductGroupFilterDisplay | null>(null);
  isPriceCrawl = input.required<boolean | undefined>();
  isGoogleSPD = input.required<boolean | undefined>();
  appendTo = input<string>('self');

  productFiltersTree = computed(() => {
    const isGoogleSPD = this.isGoogleSPD();
    const isPriceCrawl = this.isPriceCrawl();
    if (isGoogleSPD !== undefined && isPriceCrawl !== undefined) {
      return this.buildTreeNodes(isGoogleSPD, isPriceCrawl);
    } else {
      return [];
    }
  });

  selectedKeyIcon = computed(() => {
    if (this.selectedFilter()?.icon) {
      return this.selectedFilter()?.icon;
    }

    const operatorType = this.selectedFilter()?.operatorType;
    if (operatorType) {
      return this.getOperatorIconByType(operatorType);
    } else {
      return ProductGroupFilterGroups.find(
        (group) => group.filter?.key === this.selectedKey(),
      )?.icon;
    }
  });

  //#region Properties
  get keyControl(): UntypedFormControl | null {
    return this.conditionForm?.get('key') as UntypedFormControl;
  }
  //#endregion

  ngOnInit(): void {
    this.conditionForm = this._controlContainer.control as UntypedFormGroup;
    if (this.keyControl?.value) {
      if (
        this.selectedFilterControl.value?.data.key !== this.keyControl?.value
      ) {
        const node = this.getKeyNode(this.keyControl?.value);
        this.selectedFilterControl.setValue(node);
        this.selectedFilter.set(node?.data);
      }
    }

    this.keyControl?.valueChanges
      .pipe(
        startWith(this.keyControl.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newKey: string | null | undefined) => {
        this.selectedKey.set(newKey || null);
      });
  }

  onFilterNodeSelect($event: TreeNodeSelectEvent) {
    this.keyControl?.setValue($event.node.data.key);
    this.selectedFilter.set($event.node.data);
  }

  getOperatorIconByType(operatorType: string | null | undefined): string {
    switch (operatorType) {
      case 'date':
        return 'calendar_month';
      case 'integer':
        return '123';
      case 'string':
        return 'text_fields';
      case 'boolean':
        return 'check_box';
      default:
        return '';
    }
  }

  buildTreeNodes(isGoogleSPD: boolean, isPriceCrawl: boolean): TreeNode[] {
    // 1. Define the Priority Map
    const operatorTypeOrder: {[key: string]: number} = {
      integer: 2,
      // eslint-disable-next-line id-denylist
      string: 3,
      // eslint-disable-next-line id-denylist
      boolean: 4,
      date: 5,
      custom: 6,
    };
    return ProductGroupFilterGroups.filter(
      (g) =>
        (isGoogleSPD && !g.excludeInGAD) ||
        (isPriceCrawl && !g.excludeInCrawlJob) ||
        (!isGoogleSPD && !isPriceCrawl),
    ).map((group) => ({
      key: group.groupName,
      label: group.groupName,
      emIcon: group.icon || undefined,
      imageUrl: group.imageUrl || undefined,
      selectable: !!group.filter,
      data: group.filter,
      children: group.filters
        .filter(
          (filter) =>
            (isGoogleSPD && !filter.excludeInGAD) ||
            (isPriceCrawl && !filter.excludeInCrawlJob) ||
            (!isGoogleSPD && !isPriceCrawl),
        )
        .map((filter) => ({
          key: filter.key,
          label: this._translateService.instant(
            'COLUMN_' + filter.key.toUpperCase(),
          ),
          emIcon:
            filter.icon || this.getOperatorIconByType(filter.operatorType),
          data: filter,
        }))
        .sort((a, b) => {
          if (a.data.operatorType === b.data.operatorType) {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          } else {
            // custom labels always first
            const orderA =
              a.data.key === 'custom_labels'
                ? 0
                : operatorTypeOrder[a.data.operatorType || ''] || 99; // Get rank or a high default (99)
            const orderB =
              b.data.key === 'custom_labels'
                ? 0
                : operatorTypeOrder[b.data.operatorType || ''] || 99;

            return orderA - orderB;
          }
        }),
    }));
  }

  getKeyNode(key: string): TreeNode | null {
    for (const group of this.productFiltersTree()) {
      if (group.data?.key === key) {
        return group;
      }
      for (const filter of group.children || []) {
        if (filter.data?.key === key) {
          return filter;
        }
      }
    }

    return null;
  }
}
